/**
 * EmptyState — the one empty treatment.
 *
 * Four shipped: the dashed `.empty-state` box (25-odd uses), `.dashboard__empty` (a bare
 * muted paragraph, 5), `.catalog__empty` (another, 2), and two bare `<p>`s on Brands that
 * disagreed with each other about what to call the same nothing — "No brands yet." on one
 * surface and "No brands." on the other (AUD-6 MIN-3). An empty state is where a product
 * teaches its interface; four of them teach four interfaces.
 *
 * The DOM is byte-for-byte what the hand-rolled markup rendered — same classes, same
 * `aria-hidden` on the icon, same `<strong>` then `<span>` order — so the ~25 conversions
 * move no test and no probe selector. What changes is that there is now one place to fix.
 *
 * The icon is 20px here and 16px inline, and those are the only two sizes in the product
 * (G-19). It is `aria-hidden` on purpose: the `<strong>` already says what is missing, and a
 * screen reader announcing "image graphic" before it would be noise.
 */
import type { ReactNode } from "react";
import { Icon, type IconName } from "./Icon.js";

export function EmptyState({
  icon,
  title,
  hint,
  action,
  size = "md",
  testId,
  role,
}: {
  icon: IconName;
  /** The `<strong>` line: what is missing, in the product's own words. */
  title: ReactNode;
  /** The muted line: what to do about it. Omit only when there is genuinely nothing to say. */
  hint?: ReactNode;
  /** One `.btn`, optional. An empty state with a dead end is worse than none. */
  action?: ReactNode;
  /** "sm" drops the dashed box for inline / sidebar use, where a box would be a box in a box. */
  size?: "sm" | "md";
  testId?: string;
  /** `role="alert"` for the ErrorBoundary's failure card — an empty state that is a FAILURE
   *  must announce itself; the others are just absence and must not. */
  role?: string;
}) {
  return (
    <div
      className={size === "sm" ? "empty-state empty-state--sm" : "empty-state"}
      data-testid={testId}
      role={role}
    >
      <span className="empty-state__icon" aria-hidden="true">
        <Icon name={icon} size={20} />
      </span>
      <strong>{title}</strong>
      {hint !== undefined && <span>{hint}</span>}
      {action}
    </div>
  );
}
